import { taskKindLabel, type Task } from "@tlcrm/shared";
import { Link } from "react-router";

import { errorMessage } from "../api/client";
import { useUpdateTask } from "../api/hooks";
import { when } from "../lib/format";
import { useToast } from "./Toasts";

/** Open work, one row each, ticked off in place. Overdue says so in words, not just in red. */
export function TaskRows({
  tasks,
  now,
  showContact = true,
}: {
  tasks: Task[];
  now: number;
  showContact?: boolean;
}) {
  const update = useUpdateTask();
  const toast = useToast();

  const complete = async (t: Task) => {
    try {
      await update.mutateAsync({ id: t.id, version: t.version, status: "completed" });
      toast(`Done: ${t.subject}`);
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  const snooze = async (t: Task) => {
    const to = new Date(Math.max(now, t.dueAt ? Date.parse(t.dueAt) : now) + 3_600_000);
    try {
      await update.mutateAsync({ id: t.id, version: t.version, dueAt: to.toISOString() });
      toast(`Moved to ${when(to.toISOString())}`);
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  return (
    <>
      {tasks.map((t) => {
        const overdue =
          t.status === "open" && t.dueAt !== null && new Date(t.dueAt).getTime() < now;
        return (
          <div key={t.id} className={`task-row${t.status === "completed" ? " is-done" : ""}`}>
            <input
              type="checkbox"
              aria-label={`Mark "${t.subject}" done`}
              checked={t.status === "completed"}
              disabled={t.status !== "open" || update.isPending}
              onChange={() => void complete(t)}
            />
            <div style={{ minWidth: 0 }}>
              <div className="t">
                {t.priority === "high" && (
                  <span className="tag" style={{ marginRight: "var(--s-3)" }}>
                    High
                  </span>
                )}
                {t.subject}
              </div>
              <div className="s">
                {taskKindLabel(t.kind)}
                {showContact && t.contactId && t.contactName && (
                  <>
                    {" · "}
                    <Link to={`/contacts/${t.contactId}`}>{t.contactName}</Link>
                  </>
                )}
                {t.assignee && <> · {t.assignee.fullName}</>}
              </div>
            </div>
            <span className="task-due">
              <span className={`due${overdue ? " mark mark-brick" : " t3"}`}>
                {t.dueAt ? `${overdue ? "Overdue · " : ""}${when(t.dueAt)}` : "No date"}
              </span>
              {t.status === "open" && (
                <button
                  type="button"
                  className="btn btn-sm btn-quiet"
                  title="Push it an hour"
                  disabled={update.isPending}
                  onClick={() => void snooze(t)}
                >
                  +1h
                </button>
              )}
            </span>
          </div>
        );
      })}
    </>
  );
}
